export const getGroupedSectors = () => {
  return [
    {
      name: 'Cyclical',
      items: [
        'Basic Materials',
        'Consumer Cyclical',
        'Financial Services',
        'Real Estate'
      ],
      tooltip: `<div>
      <p>
        The cyclical super sector includes industries significantly impacted
        by economic shifts. When the economy is prosperous these industries
        tend to expand and when the economy is in a downturn these
        industries tend to shrink. In general, the stocks in these
        industries have betas of greater than 1.
      </p>
      <p>
        <b>Basic Materials</b> - Companies that manufacture chemicals,
        building materials and paper products. This sector also includes
        companies engaged in commodities exploration and processing.
      </p>
      <p>
        <b>Consumer Cyclical</b> - This sector includes retail stores, auto
        and auto parts manufacturers, companies engaged in residential
        construction, lodging facilities, restaurants and entertainment
        companies.
      </p>
      <p>
        <b>Financial Services</b> - Companies that provide financial
        services which includes banks, savings and loans, asset management
        companies, credit services, investment brokerage firms, and
        insurance companies.
      </p>
      <p>
        <b>Real Estate</b> - This sector includes mortgage companies, property management companies and REITs.
      </p>
    </div>`
    },
    {
      name: 'Sensitive',
      items: ['Communication Services', 'Energy', 'Industrials', 'Technology'],
      tooltip: `<div>
      <p>
        The sensitive super sector includes industries which ebb and flow
        with the overall economy, but not severely so. Sensitive industries
        fall between the defensive and cyclical industries as they are not
        immune to a poor economy but they also may not be as severely
        impacted by a poor economy as industries in the cyclical super
        sector. In general, the stocks in these industries have betas that
        are close to 1.
      </p>
      <p>
        <b>Communication Services</b> - Companies that provide communication
        services using fixed-line networks or those that provide wireless
        access and services. This sector also includes companies that
        provide internet services such as access, navigation and internet
        related software and services.
      </p>
      <p>
        <b>Energy</b> - Companies that produce or refine oil and gas, oil
        field services and equipment companies, and pipeline operators. This
        sector also includes companies engaged in the mining of coal.
      </p>
      <p>
        <b>Industrials</b> - Companies that manufacture machinery, hand-held
        hand-held tools and industrial products. This sector also includes
        aerospace and defense firms as well as companies engaged in
        transportations and logistic services.
      </p>
      <p>
        <b>Technology</b> - Companies engaged in the design, development,
        and support of computer operating systems and applications. This
        sector also includes companies that provide computer technology
        consulting services. Also includes companies engaged in the
        manufacturing of computer equipment, data storage products,
        networking products, semiconductors, and components.
      </p>
    </div>`
    },
    {
      name: 'Defensive',
      items: ['Consumer Defensive', 'Healthcare', 'Utilities'],
      tooltip: `<div>
      <p>
        The defensive super sector includes industries that are relatively
        immune to economic cycles. These industries provide services that
        consumers require in both good and bad times, such as healthcare and
        utilities. In general, the stocks in these industries have betas of
        less than 1.
      </p>
      <p>
        <b>Consumer Defensive</b> - Companies engaged in the manufacturing
        of food, beverages, household and personal products, packaging, or
        tobacco. Also includes companies that provide services such as
        education & training services.
      </p>
      <p>
        <b>Healthcare</b> - This sector includes biotechnology,
        pharmaceuticals, research services, home healthcare, hospitals,
        long-term care facilities, and medical equipment and supplies.
      </p>
      <p>
        <b>Utilities</b> - Electric, gas, and water utilities.
      </p>
    </div>`
    },
    {
      name: 'Others',
      items: ['Other']
    }
  ];
};

export const getSubSectors = () => {
  return getGroupedSectors().flatMap(group => group.items);
};

export const getGroupedRegions = () => {
  return [
    {
      name: 'Americas',
      items: ['United States', 'Canada', 'Latin America']
    },
    {
      name: 'Greater Europe',
      items: [
        'United Kingdom',
        'Eurozone',
        'Europe - ex Euro',
        'Europe - Emerging'
      ]
    },
    {
      name: 'Greater Asia',
      items: ['Asia - Developed', 'Asia - Emerging', 'Australasia', 'Japan']
    },
    {
      name: 'Other',
      items: ['Middle East', 'Africa', 'Not Classified', 'Unknown']
    }
  ];
};

export const getSubRegions = () => {
  return getGroupedRegions().flatMap(group => group.items);
};

export const shouldIncludeKey = (allocation: any) => {
  if (allocation.key && allocation.key.includes('_super_')) {
    return false;
  }

  return true;
};

export const mapSectorData = (data: any) => {
  const subSectors = getSubSectors();
  return data
    .filter((x: any) => shouldIncludeKey(x))
    .sort(
      (x: any, y: any) =>
        subSectors.findIndex(item => item === x.name) -
        subSectors.findIndex(item => item === y.name)
    );
};

export const mapRegionalData = (data: any) => {
  const mappedData = data.equity_regional_breakdown.map((equity: any) => {
    const fixedIncome = data.fixed_income_regional_breakdown.find(
      (fixedIncome: any) => fixedIncome.name === equity.name && fixedIncome
    );

    return {
      ...fixedIncome,
      ...equity,
      fixed_income: Number(fixedIncome?.value || 0) * 100,
      equity: Number(equity.value) * 100
    };
  });

  return mappedData;
};

export const mapCombinedBreakdownData = (data: any) => {
  const mappedData = data.equity_regional_breakdown.map((equity: any) => {
    const fixedIncome = data.fixed_income_regional_breakdown.find(
      (fixedIncome: any) => fixedIncome.name === equity.name && fixedIncome
    );

    return {
      ...fixedIncome,
      ...equity,
      fixed_income: Number(fixedIncome?.Existing || 0) * 100,
      equity: Number(equity.Existing) * 100
    };
  });

  return mappedData;
};
